import { Spinner, Refresh } from '@/components/icons';
import { cn } from '@/lib/utils';
import { PatientAvatar } from './PatientAvatar';

interface PatientPhotoRegenerateButtonProps {
    name: string;
    avatarUrl?: string | null;
    isLoading: boolean;
    onRegenerate: () => void;
    className?: string;
    fallbackClassName?: string;
}

export function PatientPhotoRegenerateButton({
    name,
    avatarUrl,
    isLoading,
    onRegenerate,
    className,
    fallbackClassName,
}: PatientPhotoRegenerateButtonProps) {
    return (
        <button
            type="button"
            onClick={onRegenerate}
            disabled={isLoading}
            aria-label={isLoading ? 'Regenerando foto do paciente' : 'Regenerar foto do paciente'}
            title="Regenerar foto do paciente"
            className={cn(
                'group relative flex size-16 shrink-0 items-center justify-center rounded-2xl p-0',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                'disabled:cursor-wait disabled:opacity-100',
                className,
            )}
        >
            <PatientAvatar
                name={name}
                avatarUrl={avatarUrl}
                className={cn(
                    'size-full rounded-[inherit] transition-[filter,transform] duration-200 ease-out',
                    'group-hover:scale-[1.015] group-hover:brightness-75 group-focus-visible:brightness-75',
                    isLoading && 'brightness-75',
                )}
                fallbackClassName={fallbackClassName}
            />
            <span
                aria-hidden="true"
                className={cn(
                    'pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden rounded-[inherit]',
                    'bg-black/45 text-white opacity-0 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)] backdrop-blur-[1px]',
                    'transition-[opacity,background-color] duration-200 ease-out',
                    isLoading
                        ? 'opacity-100'
                        : 'group-hover:opacity-100 group-focus-visible:opacity-100',
                )}
            >
                <span
                    className={cn(
                        'relative flex size-8 items-center justify-center rounded-full border border-white/30 bg-white/20',
                        'shadow-elevation-2 transition-[opacity,transform,filter] duration-200 ease-out',
                        isLoading
                            ? 'opacity-100'
                            : 'scale-90 opacity-90 group-hover:scale-100 group-hover:opacity-100 group-focus-visible:scale-100 group-focus-visible:opacity-100',
                    )}
                >
                    {isLoading ? (
                        <Spinner className="size-4 animate-spin" />
                    ) : (
                        <Refresh className="size-4" />
                    )}
                </span>
            </span>
        </button>
    );
}
